<template>
  <!-- INITIAL LOAD GATE: show minimal spinner while fetching primary resource.
       Prevents the page skeleton from flashing before a 404 redirect. -->
  <div
    v-if="isLoading && !detailsLoaded"
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-50 dark:bg-slate-900"
    aria-label="Loading"
    role="status"
  >
    <svg class="w-10 h-10 animate-spin text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
  </div>

  <div
    v-else
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />



    <!-- Main content -->
    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="w-full mx-auto space-y-5 max-w-7xl">
          <!-- Header + Summary -->
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div
              class="relative p-3 overflow-hidden border shadow-sm rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200/80 dark:border-slate-700/80 backdrop-blur lg:col-span-2"
            >
              <!-- faint blob -->
              <div
                class="pointer-events-none absolute -top-12 -end-10 w-40 h-40 rounded-[60px] bg-secondary/20 blur-2xl"
              ></div>

              <div class="relative z-10 flex flex-col gap-2">
                <ConsultationDetailsHeaderSkeleton
                  v-if="!detailsReady"
                  :aria-label="t('loading') || 'Loading'"
                />

                <template v-else>
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div class="flex items-center gap-2">
                    <h1
                      class="text-base font-bold text-slate-900 md:text-lg dark:text-white"
                    >
                      {{ t("consult_details_title") || "Consultation Details" }}
                    </h1>

                    <!-- number pill -->
                    <ConsultationNumberBadge
                      v-if="form.number"
                      :consultation-number="form.number"
                      :status-class="statusBadgeClass(form.status?.id)"
                      :copy-title="t('consult_copy_number')"
                    />
                  </div>

                  <div
                    class="flex flex-wrap items-center gap-2 px-2 py-1 border rounded-xl bg-white/90 dark:bg-slate-900/90 border-slate-200 dark:border-slate-700 backdrop-blur"
                  >
                    <div
                      class="flex items-center gap-2 text-xs text-slate-500 me-1 pe-2 border-e border-slate-200 dark:border-slate-700"
                    >
                      <i class="mdi mdi-flash text-[14px] text-secondary"></i>
                      {{ t("consult_table_actions") || "Actions" }}
                    </div>

                    <button
                      type="button"
                      class="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800"
                      @click="router.back()"
                    >
                      {{ t("back") || "Back" }}
                    </button>

                    <template v-if="detailsLoaded">
                      <button
                        v-if="canAccept && !rejecting"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                        :disabled="isActing"
                        @click="openAcceptFlow"
                      >
                        <i class="mdi mdi-check-circle-outline text-[14px]"></i>
                        {{
                          isActing
                            ? t("processing") || "Processing..."
                            : t("accept") || "Accept"
                        }}
                      </button>

                      <button
                        v-if="canPay"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-secondary text-white hover:bg-secondary/90 disabled:opacity-60"
                        :disabled="savingPayment"
                        @click="openPaymentModal"
                      >
                        <i class="mdi mdi-cash-multiple text-[14px]"></i>
                        {{ t("pay") || "Pay" }}
                      </button>

                      <button
                        v-if="canReject && !canPay && !isActing"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-rose-200 text-rose-600 bg-rose-50 hover:bg-rose-100 disabled:opacity-60 dark:border-rose-900/40 dark:text-rose-200 dark:bg-rose-900/30 dark:hover:bg-rose-900/50"
                        :disabled="rejecting"
                        @click="openRejectModal"
                      >
                        <i class="mdi mdi-close-circle-outline text-[14px]"></i>
                        {{
                          rejecting
                            ? t("processing") || "Processing..."
                            : t("reject") || "Reject"
                        }}
                      </button>

                      <button
                        v-if="isFinished"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-secondary/30 text-secondary bg-secondary/10 hover:bg-secondary/15 disabled:opacity-60"
                        @click="showResponseView = true"
                      >
                        {{ t("view") || "View" }}
                      </button>

                      <button
                        v-if="canWriteResponse"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-secondary text-white hover:bg-secondary/90 disabled:opacity-60"
                        @click="showResponseWrite = true"
                      >
                        <i class="mdi mdi-square-edit-outline text-[14px]"></i>
                        {{ t("write_response") || "Write response" }}
                      </button>

                      <button
                        v-if="showNegotiationEntry"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-secondary/30 text-secondary bg-secondary/10 hover:bg-secondary/15"
                        @click="openNegotiationPage"
                      >
                        <i class="mdi mdi-handshake-outline text-[14px]"></i>
                        {{ negotiationEntryLabel }}
                      </button>
                    </template>
                  </div>
                </div>

                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    t("consult_details_subtitle") ||
                    "View consultation information and respond"
                  }}
                </p>

                <!-- quick chips row -->
                <div class="flex flex-wrap items-center gap-2 mt-2">
                  <!-- Date Badge -->
                  <span
                    v-if="form.date"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                  >
                    <i class="mdi mdi-calendar-month-outline text-[13px] shrink-0"></i>
                    <span>{{ form.date }}</span>
                  </span>

                  <!-- Activity Badge -->
                  <span
                    v-if="form.activity"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-lg border border-secondary/40 text-secondary bg-secondary/5 dark:bg-secondary/20"
                  >
                    <i class="mdi mdi-domain text-[13px] shrink-0"></i>
                    <span>{{ pickLang(form.activity, "name") }}</span>
                  </span>

                  <!-- Type Badge -->
                  <span
                    v-if="form.type"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-lg border bg-white dark:bg-slate-900"
                    :class="typeBadgeClass(form.type)"
                  >
                    <i class="mdi mdi-shape-outline text-[13px] shrink-0"></i>
                    <span>{{ pickLang(form.type, "name") }}</span>
                  </span>

                  <!-- Status Badge -->
                  <span
                    v-if="detailsReady && form.status"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-lg border"
                    :class="statusBadgeClass(form.status?.id)"
                  >
                    <i
                      class="mdi text-[13px] shrink-0 opacity-90"
                      :class="consultStatusIconClass(form.status?.id)"
                    ></i>
                    <span>{{ pickLang(form.status, "title") }}</span>
                  </span>

                  <!-- Payment Status Badge -->
                  <template v-if="detailsReady && paymentDisplay.kind === 'badge'">
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-lg border"
                      :class="paymentDisplay.badgeClass"
                    >
                      <i
                        class="mdi text-[13px] shrink-0 opacity-90"
                        :class="paymentDisplay.iconClass"
                      ></i>
                      <span>{{ t(paymentDisplay.labelKey) }}</span>
                    </span>
                  </template>
                  <span
                    v-else-if="detailsReady && paymentDisplay.kind === 'text'"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-lg text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                  >
                    <span>{{ t(paymentDisplay.labelKey) }}</span>
                  </span>
                </div>

                <div class="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                  <div class="flex items-start gap-2">
                    <i class="mdi mdi-domain text-[16px] text-slate-500"></i>
                    <div>
                      <div class="text-xs text-slate-500">
                        {{ t("consult_table_activity") || "Activity" }}
                      </div>
                      <div
                        class="text-sm font-semibold text-slate-800 dark:text-slate-100"
                      >
                        {{ form.activity_description || "—" }}
                      </div>
                    </div>
                  </div>

                  <div class="flex items-start gap-2">
                    <i
                      class="mdi mdi-format-title text-[16px] text-slate-500"
                    ></i>
                    <div>
                      <div class="text-xs text-slate-500">
                        {{ t("consult_table_subject") || "Subject" }}
                      </div>
                      <div
                        class="text-sm font-semibold text-slate-800 dark:text-slate-100"
                      >
                        {{ form.subject || "—" }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-3 flex items-start gap-2">
                  <i class="mdi mdi-text text-[16px] text-slate-500"></i>
                  <div class="flex-1">
                    <div class="text-xs text-slate-500">
                      {{ t("description") || "Description" }}
                    </div>
                    <div
                      class="text-sm whitespace-pre-wrap break-words text-slate-700 dark:text-slate-200"
                    >
                      {{ form.description || "—" }}
                    </div>
                  </div>
                </div>

                <!-- Client + Contact -->
                <div
                  :class="
                    form.method !== 'none'
                      ? 'grid grid-cols-1 gap-3 mt-3 md:grid-cols-2'
                      : 'mt-3'
                  "
                >
                  <!-- Client info (integrated) -->
                  <div
                    class="p-3 rounded-xl bg-[#f3faf9] dark:bg-slate-900/30 border border-slate-200/70 dark:border-slate-700/60"
                  >
                    <div class="flex items-start gap-2">
                      <div
                        class="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/15 text-slate-700 dark:text-slate-200"
                      >
                        <i class="mdi mdi-account-tie-outline text-[16px]"></i>
                      </div>
                      <div class="flex-1">
                        <div
                          class="text-xs font-semibold tracking-widest uppercase text-slate-700 dark:text-slate-200"
                        >
                          {{ t("client_info") || "Client Information" }}
                        </div>
                        <p class="text-xs text-slate-500 dark:text-slate-400">
                          {{ t("client_info_hint") || "Request owner details" }}
                        </p>

                        <div class="mt-2 space-y-2 text-xs">
                          <div class="flex items-center justify-between gap-3">
                            <div
                              class="flex items-center gap-1 text-slate-600 dark:text-slate-300"
                            >
                              <i
                                class="mdi mdi-account-outline text-[16px]"
                              ></i>
                              <span>{{ t("client_name") || "Name" }}</span>
                            </div>
                            <span
                              class="font-semibold truncate text-slate-700 dark:text-slate-200"
                            >
                              {{ form.client_name || "-" }}
                            </span>
                          </div>

                          <div class="flex items-center justify-between gap-3">
                            <div
                              class="flex items-center gap-1 text-slate-600 dark:text-slate-300"
                            >
                              <i class="mdi mdi-domain text-[16px]"></i>
                              <span>{{
                                t("client_company") || "Company"
                              }}</span>
                            </div>
                            <span
                              class="font-semibold truncate text-slate-700 dark:text-slate-200"
                            >
                              {{ form.client_company || "-" }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Contact method -->
                  <div
                    v-if="form.method !== 'none'"
                    class="px-3 py-2 border rounded-xl bg-secondary/5 border-secondary/20"
                  >
                    <div class="flex items-center gap-2 mb-2">
                      <i
                        class="mdi mdi-phone-outline text-[14px] text-secondary"
                      ></i>
                      <div
                        class="text-xs font-semibold tracking-widest uppercase text-secondary"
                      >
                        {{ t("contact_method") || "Contact Method" }}
                      </div>
                    </div>

                    <div class="flex flex-wrap items-center gap-2 text-xs">
                      <span
                        class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-secondary/10 text-secondary border border-secondary/20"
                      >
                        <i
                          :class="
                            form.method === 'session'
                              ? 'mdi mdi-video-outline'
                              : form.method === 'chat'
                              ? 'mdi mdi-chat-processing-outline'
                              : 'mdi mdi-close-circle-outline'
                          "
                          class="text-[14px]"
                        ></i>
                        {{ methodLabel || "-" }}
                      </span>

                      <span
                        class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/70 text-slate-600 border border-secondary/20"
                      >
                        <i class="mdi mdi-calendar-month-outline"></i>
                        {{ form.method_date || "-" }}
                      </span>

                      <span
                        class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/70 text-slate-600 border border-secondary/20"
                      >
                        <i class="mdi mdi-clock-outline"></i>
                        {{ form.method_time || "-" }}
                      </span>

                      <template v-if="form.method === 'session' && isPaid">
                        <a
                          v-if="isPaid && form.method_url"
                          :href="form.method_url"
                          target="_blank"
                          class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/70 text-secondary border border-secondary/20 font-semibold hover:underline"
                        >
                          <i class="mdi mdi-link-variant text-[14px]"></i>
                          {{ t("session_link") || "Session link" }}
                        </a>
                        <span v-else-if="isPaid" class="text-slate-500">-</span>
                      </template>

                      <button
                        v-else-if="form.method === 'chat' && isPaid"
                        type="button"
                        class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/70 text-secondary border border-secondary/20 font-semibold hover:underline"
                        @click="openChatPage"
                      >
                        <i class="mdi mdi-link-variant text-[14px]"></i>
                        {{ t("chat_link") || "Chat link" }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Attachments -->
                <div
                  v-if="!detailsLoaded"
                  class="pt-3 mt-3 border-t border-slate-200/70 dark:border-slate-700/70"
                >
                  <span
                    class="block h-3 w-24 rounded bg-slate-200 dark:bg-slate-700 animate-pulse mb-3"
                  ></span>
                  <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <span
                      v-for="n in 2"
                      :key="`attach-skel-${n}`"
                      class="block h-14 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse"
                    ></span>
                  </div>
                </div>

                <div
                  v-else
                  class="pt-3 mt-3 border-t border-slate-200/70 dark:border-slate-700/70"
                >
                  <div class="flex items-center justify-between mb-2">
                    <div>
                      <h3
                        class="text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                      >
                        {{ t("attachments") || "Attachments" }}
                      </h3>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{
                          t("attachments_hint") ||
                          "Files shared by the client with the request."
                        }}
                      </p>
                    </div>

                    <span
                      class="inline-flex items-center justify-center w-7 h-7 text-xs font-semibold rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                    >
                      {{ attachments.length }}
                    </span>
                  </div>

                  <div
                    v-if="attachments.length"
                    class="grid grid-cols-1 gap-2 sm:grid-cols-2"
                  >
                    <div
                      v-for="file in attachments"
                      :key="file.id || file.tmpKey"
                      class="flex items-center justify-between gap-3 p-3 border rounded-xl bg-slate-50/70 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 w-full"
                    >
                      <div class="flex items-center min-w-0 gap-3">
                        <div
                          class="flex items-center justify-center rounded-lg w-9 h-9 bg-primary/10 text-slate-700 dark:text-slate-200"
                        >
                          <i class="mdi mdi-file-outline text-[18px]"></i>
                        </div>
                        <div class="min-w-0">
                          <div
                            class="text-xs font-medium truncate max-w-[220px] text-slate-800 dark:text-slate-100"
                          >
                            {{ fileBaseName(file.name) }}
                          </div>
                          <div
                            class="text-xs text-slate-500 dark:text-slate-400"
                          >
                            {{ fileExt(file.name) }}
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center gap-2 shrink-0">
                        <a
                          v-if="file.url"
                          :href="file.url"
                          target="_blank"
                          class="px-2 py-1 text-xs rounded-lg bg-secondary/10 text-secondary hover:underline"
                        >
                          {{ t("view") || "View" }}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div v-else class="text-xs text-slate-500">
                    {{ t("no_attachments") || "No attachments yet." }}
                  </div>
                </div>
                </template>
              </div>
            </div>

            <div class="space-y-4 lg:col-span-1">
              <ConsultationDetailsOfferSkeleton
                v-if="!detailsReady"
                :aria-label="t('loading') || 'Loading'"
              />

              <!-- Offer price + write offer -->
              <div
                v-else-if="
                  showOfferCardForConsultant &&
                  (isConsultingAccount || hasExistingOffer)
                "
                class="relative p-5 overflow-hidden border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
              >
                <div
                  class="absolute rounded-full pointer-events-none -bottom-10 -end-10 w-28 h-28 bg-primary/10"
                ></div>

                <h3
                  class="mb-3 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                >
                  {{ t("offer_details") || "تفاصيل العرض" }}
                </h3>

                <div
                  v-if="hasExistingOffer"
                  class="mb-2 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                >
                  {{ t("offer_provider_info") || "معلومات مقدم العرض" }}
                </div>

                <div
                  v-if="hasExistingOffer"
                  class="p-2 mb-3 border rounded-xl bg-slate-50/70 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700"
                >
                  <div class="flex items-center justify-between text-xs">
                    <div class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-account-tie-outline text-[16px]"></i>
                      <span>{{
                        t("field_consultant_name") || "Consultant"
                      }}</span>
                    </div>
                    <span
                      class="font-semibold text-slate-700 dark:text-slate-200"
                    >
                      {{ form.consultant_name || "-" }}
                    </span>
                  </div>
                  <div class="flex items-center justify-between mt-2 text-xs">
                    <div class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-certificate-outline text-[16px]"></i>
                      <span>{{
                        t("field_license_number") || "License Number"
                      }}</span>
                    </div>
                    <span
                      class="font-semibold text-slate-700 dark:text-slate-200"
                    >
                      {{ form.consultant_license || "-" }}
                    </span>
                  </div>
                </div>

                <div class="relative z-10 space-y-2 text-xs">
                  <div class="flex items-center justify-between">
                    <span class="text-slate-500">
                      {{ t("offer_before_tax") || "Before tax" }}
                    </span>
                    <span class="font-semibold">
                      {{
                        isConsultingAccount
                          ? offerForm.price
                          : form.offer_before_tax
                      }}
                      SAR
                    </span>
                  </div>

                  <div class="flex items-center justify-between">
                    <span class="text-slate-500">
                      {{ t("tax_percent") || "Tax %" }}
                    </span>
                    <span class="font-semibold"> {{ form.tax_percent }}% </span>
                  </div>

                  <div
                    class="flex items-center justify-between pt-2 mt-1 border-t border-slate-200 dark:border-slate-700"
                  >
                    <span class="text-slate-500">
                      {{ t("total_amount") || "Total" }}
                    </span>
                    <span
                      class="text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      {{ offerTotalWithTax.toFixed(2) }} SAR
                    </span>
                  </div>

                  <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {{
                      t("offer_hint") || "Includes consultation fee and tax."
                    }}
                  </p>
                  <!-- Consultant Offer (only for consultant role) -->
                  <div
                    v-if="isConsultingAccount && !hasExistingOffer"
                    class="relative z-10 pt-3 mt-3 border-t ..."
                  >
                    <div
                      class="mb-2 text-xs text-slate-500 dark:text-slate-400"
                    >
                      {{
                        t("consult_offer_hint") ||
                        "Set your offer price and session details then send it to the client."
                      }}
                    </div>

                    <button
                      type="button"
                      class="inline-flex items-center justify-center w-full gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90"
                      @click="showOfferModal = true"
                    >
                      <i class="mdi mdi-square-edit-outline text-[16px]"></i>
                      {{ t("send_offer") || "Send Offer" }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ConsultationDetailsBodySkeleton
            v-if="!detailsLoaded"
            :aria-label="t('loading') || 'Loading'"
          />

          <div
            v-else
            class="space-y-4"
            :class="isRefreshing ? 'opacity-70 pointer-events-none' : ''"
          >
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <!-- LEFT COLUMN -->
              <section class="space-y-4 lg:col-span-1">
                <!-- Status card -->
                <!-- <div
                  class="p-5 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
                >
                  <div class="flex items-start justify-between gap-2">
                    <div>
                      <h3
                        class="mb-1 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                      >
                        {{ t("consultation_status") || "Consultation Status" }}
                      </h3>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ t("status") || "Status" }}
                      </p>
                    </div>

                    <span
                      class="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-lg"
                      :class="statusChipClass"
                    >
                      <i
                        class="mdi text-[16px] shrink-0"
                        :class="consultStatusIconByCode(displayStatusValue)"
                      ></i>
                      {{ pickLang(form.status, "title") || "—" }}
                    </span>
                  </div>
                </div> -->

                <!-- Contact method -->
                <!-- <div
                  v-if="form.method != 'none'"
                  class="p-5 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <h3
                        class="mb-1 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                      >
                        {{ t("contact_method") || "Contact Method" }}
                      </h3>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{
                          t("contact_method_hint") ||
                          "How the consultation will be held"
                        }}
                      </p>
                    </div>

                    <div
                      class="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-slate-700 dark:text-slate-200"
                    >
                      <i
                        :class="
                          form.method === 'session'
                            ? 'mdi mdi-video-outline'
                            : form.method === 'chat'
                            ? 'mdi mdi-chat-processing-outline'
                            : 'mdi mdi-close-circle-outline'
                        "
                        class="text-[20px]"
                      ></i>
                    </div>
                  </div>

                  <div class="mt-3 space-y-2 text-xs">
                    <div class="flex items-center justify-between">
                      <span class="text-slate-500">
                        {{ t("method") || "Method" }}
                      </span>
                      <span class="font-semibold">
                        {{ methodLabel }}
                      </span>
                    </div>

                    <div class="flex items-center justify-between">
                      <span class="text-slate-500">
                        {{ t("date") || "Date" }}
                      </span>
                      <span>{{ form.method_date || "—" }}</span>
                    </div>

                    <div class="flex items-center justify-between">
                      <span class="text-slate-500">
                        {{ t("time") || "Time" }}
                      </span>
                      <span>{{ form.method_time || "—" }}</span>
                    </div>

                    <div v-if="form.method === 'session' && isPaid" class="pt-2">
                      <a
                        v-if="form.method_url"
                        :href="form.method_url"
                        target="_blank"
                        class="inline-flex items-center gap-1 text-xs font-medium text-secondary hover:underline"
                      >
                        <i class="mdi mdi-link-variant text-[16px]"></i>
                        {{ t("session_link") || "Session link" }}
                      </a>
                      <div v-else class="text-xs text-slate-500">—</div>
                    </div>

                    <div v-else-if="form.method === 'chat' && isPaid" class="pt-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 text-xs font-medium text-secondary hover:underline"
                        @click="openChatPage"
                      >
                        <i class="mdi mdi-link-variant text-[16px]"></i>
                        {{ t("chat_link") || "Chat link" }}
                      </button>
                    </div>
                  </div>
                </div> -->
              </section>

              <!-- RIGHT COLUMN -->
              <section class="space-y-4 lg:col-span-2">
                <!-- Consultant response (view/write triggers) -->

                <!-- <div
                  v-if="canWriteResponse"
                  class="p-5 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <h3
                        class="mb-1 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                      >
                        {{ t("consultant_response") || "Consultant Response" }}
                      </h3> -->
                      <!-- <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{
                          t("consultant_response_hint") ||
                          "Your written advice or notes for the client will appear here."
                        }}
                      </p> -->
                    <!-- </div>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90"
                      @click="showResponseWrite = true"
                    >
                      <i class="mdi mdi-square-edit-outline text-[14px]"></i>
                      {{ t("write_response") || "Write response" }}
                    </button>
                  </div>
                </div> -->

                <!-- Consultant response view modal -->
                <div
                  v-if="showResponseView"
                  class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                  style="margin-top: 0 !important"
                  @click.self="showResponseView = false"
                >
                  <div
                    class="w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden border shadow-xl rounded-2xl bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700 backdrop-blur"
                  >
                    <div
                      class="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-700"
                    >
                      <div class="flex items-start gap-3">
                        <div
                          class="flex items-center justify-center w-10 h-10 rounded-xl bg-secondary/10 text-secondary"
                        >
                          <i class="mdi mdi-eye-outline text-[20px]"></i>
                        </div>
                        <div>
                          <div
                            class="text-sm font-bold text-slate-900 dark:text-white"
                          >
                            {{
                              t("consultant_response") || "Consultant Response"
                            }}
                          </div>
                          <div class="text-xs text-slate-500">
                            {{
                              form.consultant_response
                                ? t("view_response_hint") ||
                                  "View the consultant response."
                                : t("no_response") ||
                                  "Response from the consultant."
                            }}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        @click="showResponseView = false"
                      >
                        <i class="mdi mdi-close"></i>
                      </button>
                    </div>

                    <div class="p-5 overflow-auto space-y-4">
                      <div
                        v-if="form.consultant_response"
                        class="p-3 text-sm whitespace-pre-wrap break-all border rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                      >
                        {{ form.consultant_response }}
                      </div>
                      <div
                        v-else
                        class="text-xs text-slate-500 dark:text-slate-400"
                      >
                        {{
                          t("no_response") || "Response from the consultant."
                        }}
                      </div>

                      <div v-if="responseAttachments.length">
                        <div
                          class="mb-2 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                        >
                          {{ t("attachments") || "Attachments" }}
                        </div>
                        <div class="space-y-2">
                          <div
                            v-for="file in responseAttachments"
                            :key="file.id"
                            class="flex items-center justify-between gap-3 p-3 border rounded-xl bg-slate-50/70 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700"
                          >
                            <div class="flex items-center min-w-0 gap-3">
                              <div
                                class="flex items-center justify-center rounded-lg w-9 h-9 bg-primary/10 text-slate-700 dark:text-slate-200"
                              >
                                <i class="mdi mdi-file-outline text-[18px]"></i>
                              </div>
                              <div class="min-w-0">
                                <div
                                  class="text-xs font-medium truncate max-w-[220px] text-slate-800 dark:text-slate-100"
                                >
                                  {{ fileBaseName(file.name) }}
                                </div>
                                <div
                                  class="text-xs text-slate-500 dark:text-slate-400"
                                >
                                  {{ fileExt(file.name) }}
                                </div>
                              </div>
                            </div>
                            <div class="flex items-center gap-2 shrink-0">
                              <a
                                v-if="file.url"
                                :href="file.url"
                                target="_blank"
                                class="px-2 py-1 text-xs rounded-lg bg-secondary/10 text-secondary hover:underline"
                              >
                                {{ t("view") || "View" }}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Consultant response write modal -->
                <div
                  v-if="showResponseWrite"
                  class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                  style="margin-top: 0 !important"
                  @click.self="showResponseWrite = false"
                >
                  <div
                    class="w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden border shadow-xl rounded-2xl bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700 backdrop-blur"
                  >
                    <div
                      class="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-700"
                    >
                      <div class="flex items-start gap-3">
                        <div
                          class="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-slate-700 dark:text-slate-200"
                        >
                          <i
                            class="mdi mdi-square-edit-outline text-[20px]"
                          ></i>
                        </div>
                        <div>
                          <div
                            class="text-sm font-bold text-slate-900 dark:text-white"
                          >
                            {{
                              t("consultant_response") || "Consultant Response"
                            }}
                          </div>
                          <div class="text-xs text-slate-500">
                            {{
                              t("consultant_response_hint") ||
                              "Your written advice or notes for the client will appear here."
                            }}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        @click="showResponseWrite = false"
                      >
                        <i class="mdi mdi-close"></i>
                      </button>
                    </div>

                    <div class="p-5 overflow-auto">
                      <div class="space-y-2">
                        <textarea
                          v-model="responseForm.text"
                          rows="6"
                          class="w-full px-3 py-2 text-sm border rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :placeholder="
                            t('write_your_response') || 'Write your response...'
                          "
                        ></textarea>

                        <div class="space-y-2">
                          <div
                            class="flex flex-col gap-2 p-3 border rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                          >
                            <div
                              class="flex items-center justify-between gap-3"
                            >
                              <div class="flex items-center gap-2 text-xs">
                                <i
                                  class="mdi mdi-paperclip text-[16px] text-slate-500"
                                ></i>
                                <div class="flex flex-col">
                                  <span
                                    class="text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                                  >
                                    {{
                                      t("add_attachment") || "Add attachment"
                                    }}
                                  </span>
                                  <span class="text-xs text-slate-500">
                                    {{ t("max_file_size") || "Max 1MB" }}
                                  </span>
                                </div>
                              </div>

                              <label
                                for="response-attachment"
                                class="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700"
                              >
                                <i class="mdi mdi-upload text-[14px]"></i>
                                {{
                                  responseForm.attachment
                                    ? t("change_file") || "Change file"
                                    : t("choose_file") || "Choose file"
                                }}
                              </label>
                            </div>

                            <input
                              id="response-attachment"
                              :key="responseAttachmentKey"
                              type="file"
                              class="sr-only"
                              @change="onResponseAttachmentChange"
                            />
                          </div>

                          <div
                            v-if="responseForm.attachment"
                            class="flex items-center justify-between gap-3 px-3 py-2 text-xs border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                          >
                            <div class="truncate">
                              <div
                                class="font-medium text-slate-700 dark:text-slate-100"
                              >
                                {{ responseForm.attachment.name }}
                              </div>
                              <div class="text-xs text-slate-500">
                                {{
                                  fileSizeLabel(responseForm.attachment.size)
                                }}
                              </div>
                            </div>
                            <button
                              type="button"
                              class="px-2 py-1 text-[10px] font-semibold rounded bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-200"
                              @click="clearResponseAttachment"
                            >
                              {{ t("remove") || "Remove" }}
                            </button>
                          </div>

                          <p
                            v-if="responseAttachmentError"
                            class="text-xs text-rose-600"
                          >
                            {{ responseAttachmentError }}
                          </p>
                        </div>

                        <button
                          type="button"
                          class="inline-flex items-center justify-center w-full gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-60"
                          :disabled="sendingResponse || !responseValid"
                          @click="sendConsultantResponse"
                        >
                          <i class="mdi mdi-send text-[16px]"></i>
                          {{
                            sendingResponse
                              ? t("processing") || "Processing..."
                              : t("contact_send") || "Send"
                          }}
                        </button>

                        <!-- <p v-if="responseError" class="text-xs text-rose-600">
                          {{ responseError }}
                        </p> -->
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ✅ ACCEPT MODAL-->
                <div
                  v-if="isModalOpen"
                  class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                  style="margin-top: 0 !important"
                  @click.self="closeModals"
                >
                  <div
                    class="w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden border shadow-xl rounded-2xl bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700 backdrop-blur"
                  >
                    <!-- header -->
                    <div
                      class="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-700 shrink-0"
                    >
                      <div class="flex items-start gap-3">
                        <div
                          class="flex items-center justify-center w-10 h-10 rounded-xl"
                          :class="
                            modalMode === 'accept'
                              ? 'bg-emerald-500/10 text-emerald-600'
                              : 'bg-primary/10 text-primary'
                          "
                        >
                          <i
                            :class="
                              modalMode === 'accept'
                                ? 'mdi mdi-check-circle-outline'
                                : 'mdi mdi-receipt'
                            "
                            class="text-[20px]"
                          ></i>
                        </div>
                        <div>
                          <div
                            class="text-sm font-bold text-slate-900 dark:text-white"
                          >
                            {{
                              modalMode === "accept"
                                ? t("accept") || "Accept"
                                : t("invoice") || "Invoice"
                            }}
                          </div>
                          <div
                            class="text-xs text-slate-500 dark:text-slate-400"
                          >
                            {{
                              modalMode === "accept"
                                ? t("accept_confirm_text") ||
                                  "Are you sure you want to accept this consultation?"
                                : t("payment_details") ||
                                  "Payment details for this consultation"
                            }}
                          </div>
                        </div>
                      </div>

                      <button
                        class="inline-flex items-center justify-center w-9 h-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                        @click="closeModals"
                        :title="t('close') || 'Close'"
                      >
                        <i
                          class="mdi mdi-close text-[18px] text-slate-600 dark:text-slate-200"
                        ></i>
                      </button>
                    </div>

                    <!-- body -->
                    <div class="flex-1 p-4 space-y-4 overflow-y-auto">
                      <!-- ✅ ACCEPT MODE: confirm text فقط -->
                      <div
                        v-if="modalMode === 'accept'"
                        class="p-4 border rounded-2xl border-slate-200 dark:border-slate-700"
                      >
                        <div class="text-sm text-slate-700 dark:text-slate-200">
                          {{
                            t("accept_confirm_text") ||
                            "By accepting, you confirm you want to proceed with this consultation."
                          }}
                        </div>

                        <div class="grid grid-cols-1 gap-3 mt-4 sm:grid-cols-2">
                          <div
                            class="p-3 border rounded-xl border-slate-200 dark:border-slate-700"
                          >
                            <div class="text-xs text-slate-500">
                              {{ t("consult_table_number") || "#" }}
                            </div>
                            <div
                              class="text-sm font-semibold text-slate-900 dark:text-white"
                            >
                              #{{ form.number || "—" }}
                            </div>
                          </div>

                          <div
                            class="p-3 border rounded-xl border-slate-200 dark:border-slate-700"
                          >
                            <div class="text-xs text-slate-500">
                              {{ t("consult_table_date") || "Date" }}
                            </div>
                            <div
                              class="text-sm font-semibold text-slate-900 dark:text-white"
                            >
                              {{ form.date || "—" }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- ✅ PAY MODE: نفس الـ Invoice + Payment details الموجودة عندك -->
                      <template v-else>
                        <!-- meta -->
                        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div
                            class="p-3 border rounded-xl border-slate-200 dark:border-slate-700"
                          >
                            <div class="text-xs text-slate-500">
                              {{ t("consult_table_number") || "#" }}
                            </div>
                            <div
                              class="text-sm font-semibold text-slate-900 dark:text-white"
                            >
                              #{{ invoiceView.number || "—" }}
                            </div>
                          </div>

                          <div
                            class="p-3 border rounded-xl border-slate-200 dark:border-slate-700"
                          >
                            <div class="text-xs text-slate-500">
                              {{ t("consult_table_date") || "Date" }}
                            </div>
                            <div
                              class="text-sm font-semibold text-slate-900 dark:text-white"
                            >
                              {{ invoiceView.date || "—" }}
                            </div>
                          </div>
                        </div>

                        <!-- totals -->
                        <div
                          class="p-4 border rounded-2xl border-slate-200 dark:border-slate-700"
                        >
                          <div
                            class="flex items-center justify-between text-xs"
                          >
                            <span class="text-slate-500">{{
                              t("offer_before_tax") || "Before tax"
                            }}</span>
                            <span
                              class="font-semibold text-slate-900 dark:text-white"
                              >{{ invoiceView.before.toFixed(2) }} SAR</span
                            >
                          </div>
                          <div
                            class="flex items-center justify-between mt-2 text-xs"
                          >
                            <span class="text-slate-500">{{
                              t("tax_percent") || "Tax %"
                            }}</span>
                            <span
                              class="font-semibold text-slate-900 dark:text-white"
                              >{{ invoiceView.taxP }}%</span
                            >
                          </div>
                          <div
                            class="flex items-center justify-between mt-2 text-xs"
                          >
                            <span class="text-slate-500">{{
                              t("tax_amount") || "Tax amount"
                            }}</span>
                            <span
                              class="font-semibold text-slate-900 dark:text-white"
                              >{{ invoiceView.taxAmount.toFixed(2) }} SAR</span
                            >
                          </div>

                          <div
                            class="flex items-center justify-between pt-3 mt-3 border-t border-slate-200 dark:border-slate-700"
                          >
                            <span
                              class="text-sm font-semibold text-slate-700 dark:text-slate-200"
                              >{{ t("total_amount") || "Total" }}</span
                            >
                            <span class="text-lg font-bold text-emerald-600"
                              >{{ invoiceView.total.toFixed(2) }} SAR</span
                            >
                          </div>
                        </div>

                        <!-- ✅ Payment details -->
                        <div
                          class="p-4 border rounded-2xl border-slate-200 dark:border-slate-700"
                        >
                          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <!-- Payment method + Platform account -->
                            <div
                              class="grid grid-cols-1 gap-3 md:col-span-2 md:grid-cols-2"
                            >
                              <!-- Payment method -->
                              <div class="space-y-1">
                                <label
                                  class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                                >
                                  {{ t("payment_method") || "Payment Method" }}
                                </label>

                                <div class="relative">
                                  <i
                                    class="mdi mdi-wallet-outline absolute top-1/2 -translate-y-1/2 text-[18px] text-slate-400"
                                    :class="
                                      lang === 'ar' ? 'right-3' : 'left-3'
                                    "
                                  ></i>

                                  <!--
                                <select
                                  v-model="paymentForm.method"
                                  class="w-full h-10 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  :class="
                                    lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'
                                  "
                                >
                                  <option value="bank_transfer" selected>
                                    {{ t("bank_transfer") || "Bank Transfer" }}
                                  </option>
                                  <option value="cash">
                                    {{ t("cash") || "Cash" }}
                                  </option>
                                </select>
                                -->
                                  <select
                                    v-model="paymentForm.method"
                                    class="w-full h-10 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    :class="
                                      lang === 'ar'
                                        ? 'pr-10 pl-3'
                                        : 'pl-10 pr-3'
                                    "
                                    aria-readonly="true"
                                    disabled
                                  >
                                    <option value="bank_transfer">
                                      {{
                                        t("bank_transfer") || "Bank Transfer"
                                      }}
                                    </option>
                                  </select>
                                </div>
                              </div>

                              <!-- Bank account (only bank transfer) -->
                              <div
                                v-if="paymentForm.method === 'bank_transfer'"
                                class="space-y-1"
                              >
                                <label
                                  class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                                >
                                  حساب المنصة
                                </label>

                                <div class="relative">
                                  <i
                                    class="mdi mdi-bank-outline absolute top-1/2 -translate-y-1/2 text-[18px] text-slate-400"
                                    :class="
                                      lang === 'ar' ? 'right-3' : 'left-3'
                                    "
                                  ></i>

                                  <!--
                                <select
                                  v-model="paymentForm.company_bank_account_id"
                                  class="w-full h-10 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  :class="
                                    lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'
                                  "
                                >
                                  <option value="" disabled>
                                    {{
                                      t("choose_account") || "Choose account"
                                    }}
                                  </option>

                                  <option
                                    v-for="acc in bankAccounts"
                                    :key="acc.id"
                                    :value="String(acc.id)"
                                  >
                                    {{ acc.bank_name }} ƒ?"
                                    {{ acc.account_name }} ({{
                                      acc.account_number
                                    }})
                                  </option>
                                </select>
                                -->
                                  <select
                                    v-model="
                                      paymentForm.company_bank_account_id
                                    "
                                    class="w-full h-10 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    :class="
                                      lang === 'ar'
                                        ? 'pr-10 pl-3'
                                        : 'pl-10 pr-3'
                                    "
                                    aria-readonly="true"
                                    disabled
                                  >
                                    <option
                                      v-if="bankAccounts.length"
                                      :value="String(bankAccounts[0].id)"
                                    >
                                      {{ bankAccounts[0].bank_name }}"
                                      {{ bankAccounts[0].account_name }}
                                      ({{ bankAccounts[0].account_number }})
                                    </option>
                                    <option v-else value="">حساب المنصة</option>
                                  </select>

                                  <p
                                    v-if="paymentErrors.company_bank_account_id"
                                    class="text-xs text-rose-600 mt-1"
                                  >
                                    {{ paymentErrors.company_bank_account_id }}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div
                              v-if="paymentForm.method === 'bank_transfer'"
                              class="space-y-1 md:col-span-2"
                            >
                              <label
                                class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                              >
                                {{ t("account_number") || "Account number" }}
                              </label>

                              <div class="relative">
                                <i
                                  class="mdi mdi-card-text-outline absolute top-1/2 -translate-y-1/2 text-[18px] text-slate-400"
                                  :class="lang === 'ar' ? 'right-3' : 'left-3'"
                                ></i>
                                <input
                                  type="text"
                                  class="w-full h-10 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  :class="
                                    lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'
                                  "
                                  :value="
                                    selectedBankAccount?.account_number || '-'
                                  "
                                  readonly
                                />
                              </div>
                            </div>

                            <!-- Account identifier -->
                            <div
                              v-if="paymentForm.method === 'bank_transfer'"
                              class="space-y-1"
                            >
                              <label
                                class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                              >
                                {{
                                  t("account_identifier") ||
                                  "Account identifier"
                                }}
                              </label>
                              <input
                                v-model="paymentForm.account_identifier"
                                type="text"
                                inputmode="numeric"
                                class="w-full h-10 px-3 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                :placeholder="
                                  t('account_identifier_placeholder') || '123'
                                "
                              />
                              <p
                                v-if="paymentErrors.account_identifier"
                                class="text-xs text-rose-600"
                              >
                                {{ paymentErrors.account_identifier }}
                              </p>
                            </div>

                            <!-- Transaction reference -->
                            <div
                              v-if="paymentForm.method === 'bank_transfer'"
                              class="space-y-1"
                            >
                              <label
                                class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                              >
                                {{
                                  t("transaction_reference") ||
                                  "Transaction reference"
                                }}
                              </label>
                              <input
                                v-model="paymentForm.transaction_reference"
                                type="text"
                                class="w-full h-10 px-3 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                :placeholder="
                                  t('transaction_reference_placeholder') ||
                                  'SA0380...'
                                "
                              />
                              <p
                                v-if="paymentErrors.transaction_reference"
                                class="text-xs text-rose-600"
                              >
                                {{ paymentErrors.transaction_reference }}
                              </p>
                            </div>

                            <div
                              v-if="paymentForm.method === 'bank_transfer'"
                              class="space-y-1 md:col-span-2"
                            >
                              <label
                                class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                              >
                                {{ t("payment_image") || "Payment image" }}
                              </label>

                              <div
                                class="p-3 border rounded-2xl border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-900/40"
                              >
                                <div class="flex items-start gap-3">
                                  <!-- icon -->
                                  <div
                                    class="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"
                                  >
                                    <i
                                      class="mdi mdi-image-outline text-[20px]"
                                    ></i>
                                  </div>

                                  <div class="flex-1 min-w-0">
                                    <div
                                      class="flex items-center justify-between gap-2"
                                    >
                                      <div class="min-w-0">
                                        <div
                                          class="text-xs font-semibold text-slate-800 dark:text-slate-100"
                                        >
                                          {{
                                            t("upload_image") ||
                                            "Upload payment proof"
                                          }}
                                        </div>
                                        <div
                                          class="text-xs text-slate-500 dark:text-slate-400"
                                        >
                                          {{
                                            t("upload_image_hint") ||
                                            "PNG/JPG/WebP — max 5MB"
                                          }}
                                        </div>
                                      </div>

                                      <!-- actions -->
                                      <div
                                        class="flex items-center gap-2 shrink-0"
                                      >
                                        <input
                                          ref="paymentFileInput"
                                          type="file"
                                          accept="image/*"
                                          class="hidden"
                                          @change="onPaymentImageChange"
                                        />

                                        <button
                                          type="button"
                                          class="inline-flex items-center justify-center gap-2 px-3 text-xs font-semibold text-white h-9 rounded-xl bg-primary hover:bg-primary/90"
                                          @click="openPaymentFilePicker"
                                        >
                                          <i
                                            class="mdi mdi-upload text-[16px]"
                                          ></i>
                                          {{
                                            t("choose_file") || "Choose file"
                                          }}
                                        </button>

                                        <button
                                          v-if="paymentForm.image?.base64"
                                          type="button"
                                          class="inline-flex items-center justify-center px-3 text-xs font-semibold border h-9 rounded-xl border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 hover:bg-slate-50 dark:hover:bg-slate-800"
                                          @click="removePaymentImage"
                                          :title="t('remove') || 'Remove'"
                                        >
                                          <i
                                            class="mdi mdi-trash-can-outline text-[16px]"
                                          ></i>
                                        </button>
                                      </div>
                                    </div>

                                    <!-- filename -->
                                    <div
                                      v-if="paymentForm.image?.name"
                                      class="mt-2 inline-flex items-center gap-2 text-xs px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                                    >
                                      <i
                                        class="mdi mdi-file-image-outline text-[14px]"
                                      ></i>
                                      <span class="truncate max-w-[260px]">{{
                                        paymentForm.image.name
                                      }}</span>
                                    </div>

                                    <!-- errors -->
                                    <p
                                      v-if="paymentErrors.image"
                                      class="mt-2 text-xs text-rose-600"
                                    >
                                      {{ paymentErrors.image }}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>

                    <!-- footer -->
                    <div
                      class="flex flex-col gap-2 p-4 border-t sm:flex-row sm:justify-end border-slate-200 dark:border-slate-700"
                    >
                      <!-- ✅ accept footer -->
                      <button
                        v-if="modalMode === 'accept'"
                        type="button"
                        class="inline-flex items-center justify-center h-10 gap-2 px-4 text-xs font-semibold text-white rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                        :disabled="isActing"
                        @click="acceptThenRefresh"
                      >
                        <i class="mdi mdi-check-circle-outline text-[16px]"></i>
                        {{
                          isActing
                            ? t("processing") || "Processing..."
                            : t("confirm_accept") || "Confirm Accept"
                        }}
                      </button>

                      <!-- ✅ pay footer -->
                      <button
                        v-if="modalMode === 'pay'"
                        type="button"
                        class="inline-flex items-center justify-center h-10 gap-2 px-4 text-xs font-semibold text-white rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-60"
                        :disabled="savingPayment"
                        @click="confirmPaymentOnly"
                      >
                        <i class="mdi mdi-cash-multiple text-[16px]"></i>
                        {{
                          savingPayment
                            ? t("processing") || "Processing..."
                            : t("pay") || "Pay"
                        }}
                      </button>

                      <button
                        type="button"
                        class="inline-flex items-center justify-center h-10 gap-2 px-4 text-xs font-semibold border rounded-xl border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 hover:bg-slate-50 dark:hover:bg-slate-800"
                        @click="closeModals"
                      >
                        {{ t("close") || "Close" }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- ✅ REJECT MODAL -->
                <div
                  v-if="showRejectModal"
                  class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                  style="margin-top: 0 !important"
                  @click.self="closeModals"
                >
                  <div
                    class="w-full max-w-md overflow-hidden border shadow-xl rounded-2xl bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700 backdrop-blur"
                  >
                    <div
                      class="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-700"
                    >
                      <div class="flex items-start gap-3">
                        <div
                          class="flex items-center justify-center w-10 h-10 rounded-xl bg-rose-500/10 text-rose-600"
                        >
                          <i
                            class="mdi mdi-close-circle-outline text-[20px]"
                          ></i>
                        </div>
                        <div>
                          <div
                            class="text-sm font-bold text-slate-900 dark:text-white"
                          >
                            {{ t("rejected") || "Rejected" }}
                          </div>
                          <div
                            class="text-xs text-slate-500 dark:text-slate-400"
                          >
                            {{
                              t("consultation_rejected_msg") ||
                              "This consultation has been rejected."
                            }}
                          </div>
                        </div>
                      </div>

                      <button
                        class="inline-flex items-center justify-center w-9 h-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                        @click="closeModals"
                      >
                        <i
                          class="mdi mdi-close text-[18px] text-slate-600 dark:text-slate-200"
                        ></i>
                      </button>
                    </div>

                    <div class="p-4 text-sm text-slate-700 dark:text-slate-200">
                      {{
                        t("reject_popup_hint") ||
                        "You can review other consultations from the list."
                      }}
                    </div>

                    <div
                      class="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-700"
                    >
                      <button
                        type="button"
                        class="inline-flex items-center justify-center h-10 gap-2 px-4 text-xs font-semibold text-white rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-60"
                        :disabled="rejecting || !canTakeAction"
                        @click="confirmReject"
                      >
                        <i class="mdi mdi-close-circle-outline text-[16px]"></i>
                        {{
                          rejecting
                            ? t("processing") || "Processing..."
                            : t("confirm_reject") || "Confirm Reject"
                        }}
                      </button>

                      <button
                        type="button"
                        class="inline-flex items-center justify-center h-10 gap-2 px-4 text-xs font-semibold border rounded-xl border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 hover:bg-slate-50 dark:hover:bg-slate-800"
                        @click="closeModals"
                      >
                        {{ t("close") || "Close" }}
                      </button>
                    </div>
                  </div>
                </div>

                <Toast
                  :show="toast.show"
                  :type="toast.type"
                  :message="toast.message"
                  @update:show="toast.show = $event"
                />
                <!-- Offer modal -->
                <div
                  v-if="showOfferModal"
                  class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                  style="margin-top: 0 !important"
                  @click.self="showOfferModal = false"
                >
                  <div
                    class="w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden border shadow-xl rounded-2xl bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700 backdrop-blur"
                  >
                    <div
                      class="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-700"
                    >
                      <div class="flex items-start gap-3">
                        <div
                          class="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary"
                        >
                          <i
                            class="mdi mdi-square-edit-outline text-[20px]"
                          ></i>
                        </div>
                        <div>
                          <div
                            class="text-sm font-bold text-slate-900 dark:text-white"
                          >
                            {{ t("offer_details") || "Offer Details" }}
                          </div>
                          <div class="text-xs text-slate-500">
                            {{
                              t("consult_offer_hint") ||
                              "Set your offer price and session details then send it to the client."
                            }}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        @click="showOfferModal = false"
                      >
                        <i class="mdi mdi-close"></i>
                      </button>
                    </div>

                    <div class="p-5 overflow-auto">
                      <!-- Live summary -->
                      <div
                        class="p-3 mb-4 border rounded-xl bg-slate-50/80 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700"
                      >
                        <div class="flex items-center justify-between text-xs">
                          <span class="text-slate-500">
                            {{ t("offer_before_tax") || "Before tax" }}
                          </span>
                          <span
                            class="font-semibold text-slate-700 dark:text-slate-200"
                          >
                            {{ Number(offerForm.price || 0).toFixed(2) }} SAR
                          </span>
                        </div>

                        <div
                          class="flex items-center justify-between mt-2 text-xs"
                        >
                          <span class="text-slate-500">
                            {{ t("tax_percent") || "Tax %" }}
                          </span>
                          <span
                            class="font-semibold text-slate-700 dark:text-slate-200"
                          >
                            {{ form.tax_percent }}%
                          </span>
                        </div>

                        <div
                          class="flex items-center justify-between pt-2 mt-2 border-t border-slate-200 dark:border-slate-700"
                        >
                          <span class="text-slate-500">
                            {{ t("total_amount") || "Total" }}
                          </span>
                          <span
                            class="text-sm font-bold text-slate-700 dark:text-slate-200"
                          >
                            {{ offerTotalWithTax.toFixed(2) }} SAR
                          </span>
                        </div>

                        <p
                          class="mt-2 text-xs text-slate-500 dark:text-slate-400"
                        >
                          {{
                            t("offer_hint") ||
                            "Includes consultation fee and tax."
                          }}
                        </p>
                      </div>

                      <!-- Price editable -->
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("offer_before_tax") || "Before tax" }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-slate-700 dark:text-slate-200"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-lg'
                              : 'left-0 rounded-l-lg'
                          "
                        >
                          <i class="mdi mdi-cash-multiple text-[16px]"></i>
                        </span>

                        <input
                          v-model.number="offerForm.price"
                          type="number"
                          min="0"
                          step="1"
                          @input="
                            offerForm.price = Math.max(
                              0,
                              Math.floor(Number(offerForm.price || 0)),
                            )
                          "
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                          placeholder="0"
                        />
                      </div>

                      <!-- Method -->
                      <div
                        v-if="canChooseOppointmentMethod"
                        class="grid grid-cols-1 gap-2 mt-3"
                      >
                        <label
                          class="block text-xs font-medium text-slate-600 dark:text-slate-300"
                        >
                          {{ t("contact_method") || "Contact Method" }}
                        </label>

                        <select
                          v-model="offerForm.oppointment_method"
                          class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        >
                          <option value="none">
                            {{ t("consult_method_none_title") || "None" }}
                          </option>

                          <option value="chat">
                            {{ t("consult_method_chat") || "Chat" }}
                          </option>

                          <option value="session">
                            {{ t("consult_method_session") || "Session" }}
                          </option>
                        </select>
                      </div>

                      <!-- Date + Time -->
                      <div
                        v-if="
                          canChooseOppointmentMethod &&
                          offerForm.oppointment_method !== 'none'
                        "
                        class="grid grid-cols-1 gap-2 mt-3 sm:grid-cols-2"
                      >
                        <div>
                          <label
                            class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                          >
                            {{ t("date") || "Date" }}
                          </label>
                          <input
                            v-model="offerForm.oppointment_date"
                            type="date"
                            class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                        </div>

                        <div>
                          <label
                            class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                          >
                            {{ t("time") || "Time" }}
                          </label>
                          <input
                            v-model="offerForm.oppointment_time"
                            type="time"
                            class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                        </div>
                      </div>

                      <!-- URL (only if session) -->
                      <div
                        v-if="
                          canChooseOppointmentMethod &&
                          offerForm.oppointment_method === 'session'
                        "
                        class="mt-3"
                      >
                        <label
                          class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                        >
                          {{ t("session_link") || "Session link" }}
                        </label>
                        <input
                          v-model="offerForm.oppointment_url"
                          type="url"
                          class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :placeholder="
                            t('consult_video_url_placeholder') || 'https://...'
                          "
                        />
                      </div>

                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-full gap-2 px-4 py-2 mt-4 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-60"
                        :disabled="sendingOffer || !offerValid"
                        @click="sendOffer"
                      >
                        <i class="mdi mdi-send text-[16px]"></i>
                        {{
                          sendingOffer
                            ? t("processing") || "Processing..."
                            : t("send_offer") || "Send Offer"
                        }}
                      </button>

                      <!-- <p v-if="offerError" class="mt-2 text-xs text-rose-600">
                        {{ offerError }}
                      </p> -->
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { messages } from "@/i18n";
import api from "@/services/api";
import {
  consultStatusIconClass,
  paymentStatusIconClass,
  consultStatusIconByCode,
} from "@/utils/consultation";
import { applyConsultationDetailsBootstrap } from "@/utils/consultationDetailsBootstrap";
import ConsultationDetailsHeaderSkeleton from "@/components/consultations/ConsultationDetailsHeaderSkeleton.vue";
import ConsultationDetailsOfferSkeleton from "@/components/consultations/ConsultationDetailsOfferSkeleton.vue";
import ConsultationDetailsBodySkeleton from "@/components/consultations/ConsultationDetailsBodySkeleton.vue";
import ConsultationNumberBadge from "@/components/consultations/ConsultationNumberBadge.vue";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";

/* ---------------- i18n (lang) ---------------- */
const initialLang = (
  document.documentElement.lang ||
  localStorage.getItem("app_lang") ||
  "ar"
).toLowerCase();

const lang = ref(initialLang.startsWith("ar") ? "ar" : "en");
function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

// keep lang synced
const _langObserver = ref(null);
const _langHandler = (e) => {
  const next = (e.detail || "ar").toLowerCase().startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
};
window.addEventListener("app:lang", _langHandler, { passive: true });

_langObserver.value = new MutationObserver((muts) => {
  for (const m of muts) {
    if (m?.attributeName === "lang" || m?.attributeName === "dir") {
      const htmlLang = (
        document.documentElement.lang ||
        localStorage.getItem("app_lang") ||
        "ar"
      ).toLowerCase();
      const next = htmlLang.startsWith("en") ? "en" : "ar";
      if (next !== lang.value) lang.value = next;
    }
  }
});
_langObserver.value.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ["lang", "dir"],
});

/* ---------------- layout ---------------- */
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");

function toggleSidebar() {
  if (window.innerWidth < 768)
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  else isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

/* ---------------- router ---------------- */
const router = useRouter();
const route = useRoute();
const id = route.params.id;

/* ---------------- auth user ---------------- */
const user = ref(null);

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function loadUserFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) return (user.value = null);
  try {
    user.value = JSON.parse(raw);
  } catch {
    user.value = null;
  }
}

const hasContactMethod = computed(() => {
  const m = String(form.method || "").toLowerCase();

  return m === "draft" || m === "none" || m === "";
});

const isClientAccount = computed(() => {
  const r = String(user.value?.role || "").toLowerCase();
  return r === "client";
});

const isConsultingAccount = computed(() => {
  const r = String(user.value?.role || "").toLowerCase();
  return r === "consultant" || r === "consultant-office";
});

const isPaid = computed(
  () => String(form.payment_status || "").toLowerCase() === "paid"
);

const showOfferCardForConsultant = computed(() => {
  return true;
});

const showPriceChip = computed(() => {
  if (!detailsReady.value) return false;
  if (Number(form.offer_before_tax || 0) > 0) return true;
  return detailsLoaded.value;
});

const displayStatusValue = computed(() => {
  const baseStatusId = Number(form.status?.id ?? 0);

  // accepted + payment_status=under_review
  if (
    baseStatusId === 4 &&
    String(form.payment_status || "").toLowerCase() === "under_review"
  ) {
    return "under_review";
  }

  return statusValue.value;
});

const displayStatusText = computed(() => {
  const baseStatusId = Number(form.status?.id ?? 0);
  const pay = String(form.payment_status || "").toLowerCase();

  if (baseStatusId === 4 && pay === "under_review") {
    return t("consult_status_under_review") || "Under Review";
  }

  return pickLang(form.status, "title") || t("status") || "Status";
});

/* ---------------- toast ---------------- */
const toast = reactive({ show: false, type: "success", message: "" });
let toastTimer = null;

function showToast(message, type = "success") {
  toast.message = message;
  toast.type = type;
  toast.show = true;

  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.show = false), 2500);
}

/* ---------------- static maps ---------------- */
const statusIdToValue = {
  1: "draft",
  2: "new",
  3: "under_study",
  4: "accepted",
  5: "finished",
};

/* ---------------- state ---------------- */
const isLoading = ref(true); // starts true so the load gate hides the skeleton on first mount
const detailsLoaded = ref(false);
const detailsBootstrapReady = ref(false);
const isActing = ref(false);

const detailsReady = computed(
  () => detailsLoaded.value || detailsBootstrapReady.value,
);

const isRefreshing = computed(() => isLoading.value && detailsLoaded.value);

function applyDetailsBootstrapFromRoute() {
  const boot = history.state?.consultationDetailsBootstrap;
  if (!boot || String(boot.consultationId) !== String(id)) return;
  if (applyConsultationDetailsBootstrap(form, boot)) {
    detailsBootstrapReady.value = true;
  }
}

const form = reactive({
  number: "",
  date: "",
  subject: "",
  description: "",

  status: null,
  payment_status: null,
  activity: null,
  type: null,

  method: "none",
  method_date: null,
  method_time: null,
  method_url: null,
  chat_url: null,

  offer_before_tax: 0,
  tax_percent: 15,

  consultant_response: "",

  client_name: "",
  client_company: "",
  client_email: "",
  client_phone: "",
  client_city: "",
  client_country: "",
  requester_user_id: null,
  created_by_user_id: null,
  consultant_name: "",
  consultant_license: "",
  negotiations_count: 0,
  can_negotiate: false,
});

applyDetailsBootstrapFromRoute();

const attachments = ref([]);
const responseAttachments = ref([]);
const paymentFileInput = ref(null);

function openPaymentFilePicker() {
  paymentFileInput.value?.click();
}

function removePaymentImage() {
  paymentForm.image = null;
  paymentErrors.image = "";
  if (paymentFileInput.value) paymentFileInput.value.value = "";
}

/* ---------------- computed helpers ---------------- */
const statusValue = computed(() => {
  const s = form.status;
  if (!s) return "";

  // Prefer stable backend keys over human titles
  const raw =
    s?.value ||
    s?.slug ||
    s?.key ||
    s?.code ||
    s?.name ||
    s?.title?.en ||
    s?.title?.ar ||
    (typeof s === "number" ? statusIdToValue[Number(s)] : null) ||
    (s?.id != null ? statusIdToValue[Number(s.id)] : null) ||
    s;

  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");
});

/* ---------------- Badges (consistent with MyConsultings.vue) ---------------- */
function statusBadgeClass(status) {
  const s = Number(status);
  if (s === 1)
    return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
  if (s === 2)
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
  if (s === 3)
    return "bg-secondary/10 text-secondary border border-secondary/50";
  if (s === 4)
    return "bg-primary/10 text-primary border border-primary/50";
  if (s === 5)
    return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
  return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
}

function typeBadgeClass(type) {
  const rawKey =
    typeof type === "object" && type !== null
      ? type?.key || type?.slug || type?.name?.en || type?.name_en || ""
      : String(type || "");
  const k = String(rawKey || "").toLowerCase();

  if (k.includes("government")) {
    return "border-rose-300 text-rose-700 dark:border-rose-700 dark:text-rose-300 bg-rose-50/60 dark:bg-rose-950/40";
  }
  if (k.includes("marketing")) {
    return "border-secondary/50 text-secondary bg-secondary/5 dark:bg-secondary/20";
  }
  return "border-primary/40 text-primary bg-primary/5";
}

/* ---------------- Payment status helpers ---------------- */
function paymentStatusLabelKey(v) {
  const s = String(v || "").toLowerCase();

  if (["under_review", "underreview", "review"].includes(s))
    return "payment_status_under_review";

  if (s === "paid") return "payment_status_paid";
  if (s === "unpaid") return "payment_status_unpaid";
  if (s === "rejected") return "payment_status_rejected";

  return "payment_status_unknown";
}

function paymentBadgeClass(v) {
  const s = String(v || "").toLowerCase();

  if (["under_review", "underreview", "review"].includes(s)) {
    return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800";
  }

  if (s === "paid") {
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
  }

  if (s === "unpaid") {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
  }

  if (s === "rejected") {
    return "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800";
  }

  return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
}

const paymentDisplay = computed(() => {
  const statusId = Number(form.status?.id);
  const pay = form.payment_status;

  if (pay != null && pay !== "") {
    return {
      kind: "badge",
      labelKey: paymentStatusLabelKey(pay),
      badgeClass: paymentBadgeClass(pay),
      iconClass: paymentStatusIconClass(pay),
    };
  }

  if (statusId === 4) {
    return {
      kind: "badge",
      labelKey: "payment_status_pending",
      badgeClass:
        "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800",
      iconClass: "mdi-clock-outline",
    };
  }

  if (statusId === 2 || statusId === 3) {
    return { kind: "text", labelKey: "payment_status_not_required" };
  }

  return { kind: "dash" };
});

const statusChipClass = computed(() => {
  switch (displayStatusValue.value) {
    case "under_review":
      return "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300";

    case "accepted":
    case "answered":
      return "bg-emerald-50 dark:bg-emerald-900/20 text-slate-700 dark:text-slate-200";

    case "rejected":
    case "closed":
      return "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300";

    case "in_progress":
    case "open":
      return "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300";

    default:
      return "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200";
  }
});

const methodLabel = computed(() => {
  if (form.method === "none") return t("consult_method_none_title") || "None";
  if (form.method === "session")
    return t("consult_method_session") || "Session";
  if (form.method === "chat") return t("consult_method_chat") || "Chat";
  return "—";
});

const totalWithTax = computed(() => {
  const before = Number(form.offer_before_tax || 0);
  const taxP = Number(form.tax_percent || 0);
  return before + before * (taxP / 100);
});

/* ---------------- access rules (CLEAN + SINGLE SOURCE) ---------------- */
const isRequester = computed(() => {
  const currentUserId = user.value?.id || user.value?.user_id || null;
  const requesterId = form.requester_user_id || form.created_by_user_id || null;

  return (
    requesterId != null &&
    currentUserId != null &&
    String(requesterId) === String(currentUserId)
  );
});

const canClientAct = computed(() => isClientAccount.value && isRequester.value);

const consultationStatusId = computed(() => Number(form.status?.id ?? 0));
const negotiationsCount = computed(() =>
  Number(form.negotiations_count ?? 0),
);

// Once a negotiation is started, accept/reject happen inside the negotiation page
const hasStartedNegotiation = computed(() => negotiationsCount.value > 0);

// 1) Accept allowed when under_study or in_progress
const canAccept = computed(() => {
  if (!canClientAct.value) return false;
  if (hasStartedNegotiation.value) return false;
  return ["under_study"].includes(statusValue.value);
});

// 2) Reject allowed when under_study or in_progress
const canReject = computed(() => {
  if (!canClientAct.value) return false;
  if (hasStartedNegotiation.value) return false;
  return ["under_study"].includes(statusValue.value);
});

// 3) Pay allowed when accepted
const canPay = computed(() => {
  if (!canClientAct.value) return false;
  if (statusValue.value !== "accepted") return false;

  const pay = String(form.payment_status || "").toLowerCase();
  if (["under_review", "paid"].includes(pay)) return false;

  return true;
});

// for old code compatibility (used by openRejectModal in template)
const canTakeAction = computed(() => canAccept.value || canReject.value);

const showPaymentBar = computed(() => canPay.value);

/* ---------------- payment + invoice ---------------- */
const paymentForm = reactive({
  consultation_id: id,
  method: "bank_transfer",
  company_bank_account_id: "",
  account_identifier: "",
  transaction_reference: "",
  invoice_id: null,
  amount: null,
  notes: "",

  image: null, // ✅ { base64: "...", name: "payment_img" }
});

const bankAccounts = ref([]);
const bankAccountsLoading = ref(false);
const bankAccountsError = ref("");

const modalMode = ref(null);
const isModalOpen = computed(() => modalMode.value !== null);
const showResponseView = ref(false);
const showResponseWrite = ref(false);
const showOfferModal = ref(false);

function closeModals() {
  modalMode.value = null;
  showRejectModal.value = false;
  showResponseView.value = false;
  showResponseWrite.value = false;
  paymentError.value = "";
  clearPaymentErrors();
}

const showRejectModal = ref(false);

const savingPayment = ref(false);
const paymentError = ref("");

const paymentErrors = reactive({
  company_bank_account_id: "",
  account_identifier: "",
  transaction_reference: "",
  image: "",
});

function clearPaymentErrors() {
  paymentErrors.company_bank_account_id = "";
  paymentErrors.account_identifier = "";
  paymentErrors.transaction_reference = "";
  paymentErrors.image = "";
}

const selectedBankAccount = computed(() => {
  if (paymentForm.method !== "bank_transfer") return null;
  const bid = String(paymentForm.company_bank_account_id || "");
  return bankAccounts.value.find((b) => String(b.id) === bid) || null;
});

const invoiceView = computed(() => {
  const before = Number(form.offer_before_tax || 0);
  const taxP = Number(form.tax_percent || 0);
  const taxAmount = before * (taxP / 100);
  const total = before + taxAmount;

  return {
    number: form.number,
    date: form.date,
    client: form.client_name,
    before,
    taxP,
    taxAmount,
    total,
    method: paymentForm.method,
    bank: selectedBankAccount.value,
  };
});

const paymentValid = computed(() => {
  if (!canPay.value) return false;
  if (!paymentForm.method) return false;

  if (paymentForm.method === "bank_transfer") {
    if (!paymentForm.company_bank_account_id) return false;
    if (!String(paymentForm.account_identifier || "").trim()) return false;
    if (!String(paymentForm.transaction_reference || "").trim()) return false;
    if (!paymentForm.image?.base64) return false;
  }
  return true;
});

async function openPaymentModal() {
  paymentError.value = "";
  clearPaymentErrors();
  await fetchDetails({ silent: true });
  await resolveInvoiceId();

  paymentForm.method = "bank_transfer";

  if (paymentForm.method === "bank_transfer" && !bankAccounts.value.length) {
    await fetchBankAccounts();
  }

  if (paymentForm.method === "bank_transfer" && bankAccounts.value.length) {
    paymentForm.company_bank_account_id = String(bankAccounts.value[0].id);
  }

  modalMode.value = "pay"; // ✅ invoice + payment details
}

async function openAcceptFlow() {
  paymentError.value = "";
  clearPaymentErrors();

  modalMode.value = "accept";
}

function validatePaymentFields() {
  clearPaymentErrors();

  if (!paymentForm.method) {
    paymentError.value =
      t("choose_payment_data") || "Please select payment data.";
    return false;
  }

  if (paymentForm.method === "bank_transfer") {
    if (!String(paymentForm.company_bank_account_id || "").trim()) {
      paymentErrors.company_bank_account_id =
        t("choose_account_required") || "Please choose a bank account.";
    }

    if (!String(paymentForm.account_identifier || "").trim()) {
      paymentErrors.account_identifier =
        t("account_identifier_required") || "Account identifier is required.";
    } else if (
      !/^\d{1,30}$/.test(String(paymentForm.account_identifier).trim())
    ) {
      paymentErrors.account_identifier =
        t("account_identifier_invalid") ||
        "Account identifier must be numbers only.";
    }

    if (!String(paymentForm.transaction_reference || "").trim()) {
      paymentErrors.transaction_reference =
        t("transaction_reference_required") ||
        "Transaction reference is required.";
    } else if (String(paymentForm.transaction_reference).trim().length < 8) {
      paymentErrors.transaction_reference =
        t("transaction_reference_invalid") ||
        "Transaction reference is too short.";
    }
    if (!paymentForm.image?.base64) {
      paymentErrors.image =
        t("payment_image_required") || "Payment image is required.";
    }
  }

  const hasAny =
    paymentErrors.company_bank_account_id ||
    paymentErrors.account_identifier ||
    paymentErrors.transaction_reference ||
    paymentErrors.image;

  return !hasAny;
}
function base64ToFile(base64, filename = "proof.jpg") {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const ext = (filename.split(".").pop() || "jpg").toLowerCase();
  const mime =
    ext === "png" ? "image/png" : ext === "webp" ? "image/webp" : "image/jpeg";

  return new File([ab], filename, { type: mime });
}

/* ---- invoice resolve (single source) ---- */
const invoiceIdLoading = ref(false);
const invoiceIdError = ref("");

function buildInvoiceCandidates(consultNumberRaw) {
  const n = String(consultNumberRaw || "").trim();

  const candidates = new Set();

  // الحالة الطبيعية: CON-2025-00007 -> CON-2025-00007-INV
  if (n) candidates.add(`${n}-INV`);

  // لو أصلاً جاي فيه -INV
  if (n.endsWith("-INV")) candidates.add(n);

  // لو جاي فيه # أو مسافات
  candidates.add(n.replace(/^#/, "").trim() + "-INV");

  return [...candidates].map((x) => x.replace(/\s+/g, "").trim());
}

async function resolveInvoiceId() {
  invoiceIdError.value = "";
  return paymentForm.invoice_id || null;
}

/* ---- submit payment ---- */
async function confirmPaymentOnly() {
  paymentError.value = "";

  if (!validatePaymentFields()) {
    showToast(
      t("please_check_required_fields") || "Please check required fields.",
      "error",
    );
    return;
  }

  if (savingPayment.value) return;
  savingPayment.value = true;

  try {
    const invId = await resolveInvoiceId();
    if (!invId) {
      showToast(
        t("invoice_not_found") || "Invoice not found for this consultation.",
        "error",
      );
      return;
    }

    const payload = {
      paymentMethod:
        paymentForm.method === "bank_transfer" ? "bank transfer" : "cash",
      company_bank_account_id: paymentForm.company_bank_account_id
        ? String(paymentForm.company_bank_account_id)
        : null,
      account_identifier: String(paymentForm.account_identifier || "").trim(),
      transaction_reference: String(
        paymentForm.transaction_reference || "",
      ).trim(),

      // ✅ IMPORTANT: server expects array/object, not file
      proof_image:
        paymentForm.method === "bank_transfer"
          ? {
              base64: paymentForm.image?.base64, // already pure base64 in your code
              name: paymentForm.image?.name || "proof.jpg",
            }
          : null,
    };

    await api.post(`/payments/${invId}`, payload, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    // local optimistic update
    form.payment_status = "under_review";

    showToast(t("payment_saved") || "Payment saved successfully.", "success");
    closeModals();
  } catch (e) {
    const serverErrors = e?.response?.data?.errors || {};

    paymentErrors.company_bank_account_id =
      serverErrors.company_bank_account_id?.[0] || "";
    paymentErrors.account_identifier =
      serverErrors.account_identifier?.[0] || "";
    paymentErrors.transaction_reference =
      serverErrors.transaction_reference?.[0] || "";

    // ✅ likely server uses proof_image.*
    paymentErrors.image =
      serverErrors["proof_image.base64"]?.[0] ||
      serverErrors["proof_image"]?.[0] ||
      serverErrors.image?.[0] ||
      "";

    paymentError.value =
      e?.response?.data?.message ||
      t("payment_error") ||
      "Failed to save payment.";
    showToast(paymentError.value, "error");
  } finally {
    savingPayment.value = false;
  }
}

/* ---------------- accept/reject actions ---------------- */
async function acceptConsultationOnly() {
  if (!canAccept.value || isActing.value) return;

  isActing.value = true;
  try {
    await api.post(`/consultations/${id}/accept`);

    // local optimistic update
    form.status = {
      id: 4,
      value: "accepted",
      title: { ar: "مقبولة", en: "Accepted" },
    };

    showToast(t("accepted_success") || "Accepted successfully.", "success");
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      "Failed to accept.";
    showToast(msg, "error");
  } finally {
    isActing.value = false;
  }
}

const rejecting = ref(false);

function openRejectModal() {
  if (!canReject.value) return;
  paymentError.value = "";
  showRejectModal.value = true;
}

async function confirmReject() {
  if (!canReject.value) return;
  if (rejecting.value) return;

  rejecting.value = true;
  try {
    await api.post(`/consultations/${id}/reject`);

    form.status = {
      id: 7,
      value: "rejected",
      title: { ar: "مرفوضة", en: "Rejected" },
    };

    showToast(t("rejected_success") || "Rejected successfully.", "success");
    showRejectModal.value = false;

    // optional refresh
    await fetchDetails();
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      "Failed to reject.";
    showToast(msg, "error");
  } finally {
    rejecting.value = false;
  }
}

/* ---------------- payment image handler ---------------- */
function fileToBase64Only(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      // data:image/png;base64,XXXX -> XXXX
      const base64 = result.includes("base64,")
        ? result.split("base64,")[1]
        : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function onPaymentImageChange(e) {
  const file = e?.target?.files?.[0];
  if (!file) return;

  if (!file.type.startsWith("image/")) {
    paymentForm.image = null;
    paymentErrors.image = t("invalid_file_type") || "Invalid image file.";
    return;
  }

  const maxMb = 5;
  if (file.size > maxMb * 1024 * 1024) {
    paymentForm.image = null;
    paymentErrors.image = t("file_too_large") || `Max ${maxMb}MB.`;
    return;
  }

  const base64 = await fileToBase64Only(file);

  paymentForm.image = {
    base64,
    name: file.name || "payment_img",
  };
}

/* ---------------- consultant offer + response ---------------- */
const sendingOffer = ref(false);
const offerError = ref("");

const offerForm = reactive({
  price: 0,
  oppointment_method: "none",
  oppointment_date: "",
  oppointment_time: "",
  oppointment_url: "",
});

const canChooseOppointmentMethod = computed(() => {
  const method = String(form.method || "none").toLowerCase();
  return method === "none" || method === "";
});

const effectiveOfferMethod = computed(() =>
  canChooseOppointmentMethod.value ? offerForm.oppointment_method : form.method,
);

const effectiveOfferDate = computed(() =>
  canChooseOppointmentMethod.value
    ? offerForm.oppointment_date
    : form.method_date,
);

const effectiveOfferTime = computed(() =>
  canChooseOppointmentMethod.value
    ? offerForm.oppointment_time
    : form.method_time,
);

const effectiveOfferUrl = computed(() =>
  canChooseOppointmentMethod.value
    ? offerForm.oppointment_url
    : form.method_url,
);

const offerValid = computed(() => {
  if (!isConsultingAccount.value) return false;
  if (offerForm.price == null || Number(offerForm.price) <= 0) return false;

  const method = String(effectiveOfferMethod.value || "none").toLowerCase();

  // If method is none, no need date/time/url
  if (method === "none") return true;

  // chat/session require date+time
  if (!effectiveOfferDate.value || !effectiveOfferTime.value) return false;

  // session requires url
  if (method === "session" && !String(effectiveOfferUrl.value || "").trim())
    return false;

  return true;
});

async function sendOffer() {
  offerError.value = "";
  if (!offerValid.value) {
    offerError.value =
      t("please_check_required_fields") || "Please check required fields.";
    showToast(offerError.value, "error");
    return;
  }

  sendingOffer.value = true;
  try {
    const method = String(effectiveOfferMethod.value || "none").toLowerCase();
    const date = method === "none" ? "" : effectiveOfferDate.value;
    const time = method === "none" ? "" : effectiveOfferTime.value;
    const url =
      method === "session" ? String(effectiveOfferUrl.value || "").trim() : "";

    await api.put(`/consultations/${id}/offer`, {
      price: Number(offerForm.price),
      oppointment_method: method,
      oppointment_date: date,
      oppointment_time: time,
      oppointment_url: url,
    });

    // update local
    form.offer_before_tax = Number(offerForm.price);
    form.method = method;
    form.method_date = date || null;
    form.method_time = time || null;
    form.method_url = method === "session" ? url : null;

    showToast(t("offer_sent_success") || "Offer sent successfully.", "success");
    showOfferModal.value = false;
    await fetchDetails();
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      "Failed to send offer.";
    offerError.value = msg;
    showToast(msg, "error");
  } finally {
    sendingOffer.value = false;
  }
}

const hasExistingOffer = computed(() => Number(form.offer_before_tax || 0) > 0);

const offerTotalWithTax = computed(() => {
  const before = Number(offerForm.price || 0);
  const taxP = Number(form.tax_percent || 0);
  return before + before * (taxP / 100);
});

const canWriteResponse = computed(() => {
  if (!isConsultingAccount.value) return false;

  const baseStatusId = Number(form.status?.id ?? 0);
  const pay = String(form.payment_status || "").toLowerCase();

  return baseStatusId === 4 && pay === "paid";
});

const responseForm = reactive({ text: "", attachment: null });
const responseAttachmentKey = ref(0);
const responseAttachmentError = ref("");
const sendingResponse = ref(false);
const responseError = ref("");

const responseValid = computed(() => {
  const hasText = String(responseForm.text || "").trim().length > 0;
  const hasFile = !!responseForm.attachment;
  return hasText || hasFile;
});

const isFinished = computed(() => {
  const id = Number(form.status?.id ?? 0);
  return id === 5 || statusValue.value === "finished";
});

function onResponseAttachmentChange(e) {
  const file = e?.target?.files?.[0];
  if (!file) return;

  const maxMb = 10;
  if (file.size > maxMb * 1024 * 1024) {
    responseForm.attachment = null;
    responseAttachmentError.value = t("file_too_large") || `Max ${maxMb}MB.`;
    responseAttachmentKey.value += 1;
    return;
  }

  responseForm.attachment = file;
  responseAttachmentError.value = "";
}

function clearResponseAttachment() {
  responseForm.attachment = null;
  responseAttachmentKey.value += 1;
  responseAttachmentError.value = "";
}

async function sendConsultantResponse() {
  responseError.value = "";
  if (!responseValid.value) {
    responseError.value =
      t("please_check_required_fields") ||
      "Please write a response or add an attachment.";
    showToast(responseError.value, "error");
    return;
  }
  if (responseAttachmentError.value) {
    showToast(responseAttachmentError.value, "error");
    return;
  }

  if (sendingResponse.value) return;
  sendingResponse.value = true;

  try {
    // const fd = new FormData();
    // fd.append("consultant_response", String(responseForm.text || "").trim());
    // if (responseForm.attachment) {
    //   fd.append("attachment", responseForm.attachment);
    // }
    const fd = new FormData();
    fd.append("_method", "PUT"); // ✅ important
    fd.append("consultant_response", String(responseForm.text || "").trim());

    if (responseForm.attachment) {
      fd.append("attachment", responseForm.attachment);
    }

    await api.post(`/consultations/${id}/response`, fd, {
      headers: {
        Accept: "application/json",
        // "Content-Type": "multipart/form-data",
      },
    });

    form.consultant_response = String(responseForm.text || "").trim();
    responseForm.attachment = null;
    responseAttachmentKey.value += 1;

    showToast(
      t("response_sent_success") || "Response sent successfully.",
      "success",
    );
    showResponseWrite.value = false;
    await fetchDetails();
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      "Failed to send response.";
    responseError.value = msg;
    showToast(msg, "error");
  } finally {
    sendingResponse.value = false;
  }
}

/* ---------------- misc helpers ---------------- */
function pickLang(obj, field = "title") {
  if (!obj) return "—";
  if (typeof obj === "string") return t(obj);
  const val = obj[field];
  if (val && typeof val === "object")
    return val[lang.value] || val.en || val.ar || "—";
  return val || "—";
}

function fileSizeLabel(bytes) {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return kb.toFixed(1) + " KB";
  return (kb / 1024).toFixed(1) + " MB";
}

async function getRemoteFileSizeLabel(url) {
  // if (!url) return "â€”";
  if (!url) return "";

  try {
    const res = await fetch(url, { method: "HEAD" });
    const len = res.headers.get("content-length");
    const bytes = Number(len || 0);
    return fileSizeLabel(bytes);
  } catch (e) {
    // return "â€”";
    return "";
  }
}

function fileExt(name = "") {
  const parts = name.split(".");
  return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
}

function fileBaseName(name = "") {
  const clean = String(name || "");
  if (!clean.includes(".")) return clean;
  return clean.split(".").slice(0, -1).join(".") || clean;
}

function isImage(file) {
  const name = String(file.name || "").toLowerCase();
  return (
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".gif") ||
    name.endsWith(".webp")
  );
}

function filePreview(file) {
  if (file.url) return file.url;
  if (file.file) return URL.createObjectURL(file.file);
  return "";
}

function fileNameFromUrl(url = "") {
  const clean = decodeURIComponent(String(url).split("?")[0] || "");
  return clean.split("/").pop() || "attachment";
}

function openChatPage() {
  router.push({ name: "ConsultationChat", params: { id } });
}

function openNegotiationPage() {
  router.push({
    name: "ConsultationNegotiation",
    params: { id },
    state: {
      negotiationBootstrap: {
        consultationId: String(id),
        can_negotiate: form.can_negotiate,
        negotiations_count: Number(form.negotiations_count ?? 0),
        negotiation_round: Number(
          form.negotiation_round ??
            Math.min(
              3,
              Math.floor(Number(form.negotiations_count ?? 0) / 2) + 1,
            ),
        ),
        offer_before_tax: Number(
          form.offer_before_tax ?? form.offer_price ?? 0,
        ),
        tax_percent: Number(form.tax_percent ?? 15),
        statusId: consultationStatusId.value,
        subject: form.subject || "",
        number: String(form.consultation_number || form.number || "").trim(),
      },
    },
  });
}

/**
 * status === 3 → Negotiate
 * - Client can start even with count 0
 * - Consultant only after client sent the first offer (count > 0)
 * count > 0 && status !== 3 → View Negotiation
 */
const showNegotiateEntry = computed(() => {
  if (consultationStatusId.value !== 3) return false;
  if (isConsultingAccount.value) return negotiationsCount.value > 0;
  return true;
});
const showViewNegotiationEntry = computed(
  () =>
    negotiationsCount.value > 0 && consultationStatusId.value !== 3,
);
const showNegotiationEntry = computed(
  () => showNegotiateEntry.value || showViewNegotiationEntry.value,
);
const negotiationEntryLabel = computed(() => {
  if (showViewNegotiationEntry.value) {
    return t("view_negotiation") || "View Negotiation";
  }
  return t("negotiate") || "Negotiate";
});

function openInvoicesPage() {
  router.push({ name: "MyInvoices" });
}

/* ---------------- watchers ---------------- */
watch(
  () => form.method,
  (v) => {
    if (v === "none") {
      form.method_date = null;
      form.method_time = null;
      form.method_url = null;
    }
    if (v !== "session") form.method_url = null;
  },
  { immediate: true },
);

// clear inline errors
watch(
  () => paymentForm.company_bank_account_id,
  () => (paymentErrors.company_bank_account_id = ""),
);
watch(
  () => paymentForm.account_identifier,
  () => (paymentErrors.account_identifier = ""),
);
watch(
  () => paymentForm.transaction_reference,
  () => (paymentErrors.transaction_reference = ""),
);
watch(
  () => paymentForm.image,
  () => (paymentErrors.image = ""),
);

// if payment method switches to cash, clear bank-only fields
watch(
  () => paymentForm.method,
  async (m) => {
    if (m === "cash") {
      paymentForm.company_bank_account_id = "";
      paymentForm.account_identifier = "";
      paymentForm.transaction_reference = "";
      paymentForm.image = null;
      clearPaymentErrors();
      return;
    }

    if (!bankAccounts.value.length) {
      await fetchBankAccounts();
    }
    if (!paymentForm.company_bank_account_id && bankAccounts.value.length) {
      paymentForm.company_bank_account_id = String(bankAccounts.value[0].id);
    }
  },
);

watch(canPay, (v) => {
  console.log(
    "canPay changed:",
    v,
    "status:",
    statusValue.value,
    "requester:",
    form.requester_user_id,
    "user:",
    user.value?.id || user.value?.user_id,
  );
});

/* ---------------- API ---------------- */
async function fetchDetails(options = {}) {
  const { silent = false } = options;
  if (!silent) isLoading.value = true;
  try {
    const res = await api.get(`/consultations/${id}`);
    const c = res.data?.data || res.data;
    paymentForm.invoice_id = c.invoice?.id ?? c.invoice_id ?? null;

    const handledBy = c.handled_by || null;

    form.consultant_name = handledBy?.user?.name || handledBy?.name || "";

    form.consultant_license = handledBy?.license_number || "";

    form.number = String(c.consultation_number || c.number || "").trim();
    form.date = c.date || "";
    form.subject = c.subject || "";
    form.description = c.description || "";

    form.status = c.status || null;
    form.payment_status = c.payment_status || null;
    form.negotiations_count = Number(c.negotiations_count ?? 0);
    form.can_negotiate = !!c.can_negotiate;
    form.activity = c.activity || null;

    form.type = c.type || null;

    form.method = c.oppointment_method;
    console.log("this is the form method", form.method);
    form.method_date = c.oppointment_date || c.method_date || null;
    form.method_time = c.oppointment_time || c.method_time || null;
    form.method_url = c.oppointment_url || c.method_url || null;
    form.chat_url = c.chat_url || c.room_url || c.chat_link || null;

    const resolvedPrice =
      c.offer_before_tax ??
      c.offer_price_before_tax ??
      c.offer_price ??
      c.price ??
      0;

    form.offer_before_tax = Number(resolvedPrice || 0);
    form.tax_percent = Number(c.tax_percent ?? 15);

    // prefill offer form
    offerForm.price = Number(form.offer_before_tax || 0);
    offerForm.oppointment_method = form.method;
    offerForm.oppointment_date = form.method_date || "";
    offerForm.oppointment_time = form.method_time || "";
    offerForm.oppointment_url = form.method_url || "";

    form.consultant_response = c.consultant_response || c.response || "";
    responseForm.text = form.consultant_response || "";

    const requesterUserId =
      c.created_by?.user?.id ??
      c.requester_user_id ??
      c.created_by_user_id ??
      null;

    form.requester_user_id = requesterUserId;
    form.created_by_user_id = c.created_by_user_id ?? null;

    const requester = c.created_by || null;
    const requesterUser = requester?.user || null;

    form.client_name =
      c.client_name ||
      c.requester_name ||
      requesterUser?.name ||
      requesterUser?.full_name ||
      requester?.name ||
      requester?.full_name ||
      requester?.manager_name ||
      "";

    form.client_company =
      c.client_company ||
      c.company_name ||
      requester?.company_name ||
      requester?.company?.name ||
      requester?.company ||
      requester?.activity ||
      "";
    form.activity_description = requester?.activity || "";
    form.client_email =
      c.client_email ||
      c.requester_email ||
      requesterUser?.email ||
      requester?.email ||
      "";

    form.client_phone =
      c.client_phone ||
      c.requester_phone ||
      requesterUser?.phone ||
      requesterUser?.mobile ||
      requester?.phone ||
      requester?.mobile ||
      "";

    form.client_city =
      c.client_city ||
      requester?.city ||
      requester?.address?.city ||
      requesterUser?.address?.city ||
      "";

    form.client_country =
      c.client_country ||
      requester?.country ||
      requester?.address?.country ||
      requesterUser?.address?.country ||
      "";

    attachments.value = (c.attachments || []).map((a) => {
      const url = a.url || a;
      const name =
        a.name ||
        a.file_name ||
        (typeof url === "string" ? fileNameFromUrl(url) : "attachment");
      return {
        id: a.id || name,
        name: name === "-" ? "attachment" : name,
        url,
        sizeLabel: fileSizeLabel(a.size),
      };
    });

    const responseFilesRaw =
      c.response_attachments ||
      c.consultant_response_attachments ||
      c.consultant_response_attachment ||
      c.consultant_response_files ||
      c.response_files ||
      [];

    const responseFiles = Array.isArray(responseFilesRaw)
      ? responseFilesRaw
      : responseFilesRaw
      ? [responseFilesRaw]
      : [];

    responseAttachments.value = responseFiles.map((a) => {
      if (typeof a === "string") {
        const name = fileNameFromUrl(a);
        return {
          id: name,
          name: name === "-" ? "attachment" : name,
          url: a,
          sizeLabel: "-",
        };
      }
      const url = a.url || a.file || a.path || a;
      const size =
        a.size ?? a.file_size ?? a.size_bytes ?? a.filesize ?? a.length ?? 0;
      const name =
        a.name ||
        a.file_name ||
        (typeof url === "string" ? fileNameFromUrl(url) : "attachment");
      return {
        id: a.id || name,
        name: name === "-" ? "attachment" : name,
        url,
        sizeLabel: fileSizeLabel(size),
      };
    });
    await Promise.all(
      responseAttachments.value.map(async (file) => {
        if (!file.url) return;
        if (file.sizeLabel !== "-") return;
        const label = await getRemoteFileSizeLabel(file.url);
        file.sizeLabel = label;
      }),
    );
    console.log("STATUS RAW:", c.status);
    console.log("STATUS PAYMENT:", c.payment_status);
    console.log("STATUS ID:", c.status?.id, "STATUS VALUE:", c.status?.value);
    console.log("FULL CONSULTATION:", c);
    console.log("consultant_response:", c.consultant_response);
    console.log("response:", c.response);
    console.log("consultantResponse:", c.consultantResponse);
    console.log("consultant_reply:", c.consultant_reply);

    // When number becomes available, try resolve invoice id (silent)
    if (form.number && !paymentForm.invoice_id) {
      resolveInvoiceId();
    }
  } catch (e) {
    // 404 is handled globally by the Axios interceptor in api.js.
    if (e?.response?.status !== 404) {
      console.error(e);
    }
  } finally {
    if (!silent) isLoading.value = false;
    detailsLoaded.value = true;
  }
}

async function fetchBankAccounts() {
  bankAccountsLoading.value = true;
  bankAccountsError.value = "";

  try {
    const res = await api.get("/companyBankAccounts");
    const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
    bankAccounts.value = Array.isArray(list) ? list : [];
  } catch (e) {
    bankAccountsError.value =
      e?.response?.data?.message || "Failed to load bank accounts.";
    // fallback static (optional)
    bankAccounts.value = [
      {
        id: 1,
        account_name: "منصة الاستشارات الذكية",
        account_number: "2333333",
        bank_name: "البنك الأهلي السعودي",
      },
    ];
  } finally {
    bankAccountsLoading.value = false;
  }
}
async function acceptThenRefresh() {
  if (!canAccept.value || isActing.value) return;

  isActing.value = true;
  try {
    await api.post(`/consultations/${id}/accept`);

    // local optimistic update
    form.status = {
      id: 4,
      value: "accepted",
      title: { ar: "مقبولة", en: "Accepted" },
    };

    closeModals();
    showToast(t("accepted_success") || "Accepted successfully.", "success");
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      "Failed to accept.";
    showToast(msg, "error");
  } finally {
    isActing.value = false;
  }
}

/* ---------------- lifecycle ---------------- */
onMounted(async () => {
  loadUserFromCookie();
  await fetchDetails();

  if (paymentForm.method === "bank_transfer") {
    await fetchBankAccounts();
  }
});

onBeforeUnmount(() => {
  if (_langObserver.value) _langObserver.value.disconnect();
  window.removeEventListener("app:lang", _langHandler);
  if (toastTimer) clearTimeout(toastTimer);
});
</script>
